// Het betaalscherm op #/betalen/<token>: QR-code, rekening en mededeling.
// De link blijft werken na herladen, zodat de gast later kan betalen.
import qrcode from 'qrcode-generator';
import type { OrderView } from '../shared/api-types.ts';
import { cleanIban, epcPayload, prettyIban } from '../shared/payment.ts';
import { claimPaid, getOrder } from './api.ts';
import { $, add, formatEuro, h, toast } from './dom.ts';

const COPIED_MS = 1800;

// Standaard knipt qrcode-generator tekens af tot 1 byte; de EPC-code is UTF-8.
qrcode.stringToBytes = (s: string): number[] => Array.from(new TextEncoder().encode(s));

function qrSvg(text: string): HTMLElement {
  const q = qrcode(0, 'M');
  q.addData(text, 'Byte');
  q.make();
  const box = h('div', { class: 'qr', role: 'img', 'aria-label': 'Betaal-QR-code: scan met je bankapp' });
  // R7: innerHTML enkel voor de SVG van qrcode-generator, nooit voor gebruikerstekst.
  box.innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  return box;
}

function copyBtn(text: string, label: string): HTMLButtonElement {
  const btn = h('button', { class: 'btn ghost small', type: 'button' }, label);
  btn.addEventListener('click', () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        btn.textContent = 'Gekopieerd';
        setTimeout(() => { btn.textContent = label; }, COPIED_MS);
      })
      .catch(() => {
        const target = btn.previousElementSibling;
        const sel = getSelection();
        if (!target || !sel) return;
        const range = document.createRange();
        range.selectNodeContents(target);
        sel.removeAllRanges();
        sel.addRange(range);
      });
  });
  return btn;
}

function field(label: string, value: string, extra?: Node | null, cls = 'field'): HTMLElement {
  return h('div', { class: cls }, h('span', { text: label }), h('div', { class: 'val' }, h('b', { text: value }), extra));
}

function statusNote(o: OrderView): HTMLElement | null {
  if (o.status === 'overgeschreven') return h('p', { class: 'note-box', text: 'Bedankt! We laten weten wanneer het binnen is.' });
  if (o.status === 'ontvangen') return h('p', { class: 'note-box', text: 'Je overschrijving is goed aangekomen. Dankjewel!' });
  return null;
}

function renderOrder(view: HTMLElement, o: OrderView, token: string): void {
  const ok = cleanIban(o.iban) !== '';
  const titles = o.lines.map((l) => l.title).join(', ');
  const fields = h('div', { class: 'fields' },
    field('Bedrag', formatEuro(o.totalCents), null, 'field amount'),
    field('Naar', o.accountName),
    field('Rekeningnummer', ok ? prettyIban(o.iban) : '[IBAN nog in te vullen]', ok ? copyBtn(cleanIban(o.iban), 'Kopieer') : null),
    field('Mededeling', o.reference, copyBtn(o.reference, 'Kopieer')));
  const paid = h('button', { class: 'btn', type: 'button', disabled: !ok || o.status !== 'beloofd' }, 'Ik heb overgeschreven');
  paid.addEventListener('click', () => {
    paid.disabled = true;
    claimPaid(token)
      .then(() => {
        toast('Bedankt! We laten weten wanneer het binnen is.');
        renderOrder(view, { ...o, status: 'overgeschreven' }, token);
      })
      .catch(() => {
        paid.disabled = false;
        toast('Dat lukte niet. Probeer het opnieuw.');
      });
  });
  const later = h('a', { class: 'btn ghost', href: '#/' }, o.status === 'beloofd' ? 'Ik betaal later' : 'Terug naar de lijst');
  later.addEventListener('click', () => {
    if (o.status === 'beloofd') toast('Dankjewel! Vergeet je overschrijving niet.');
  });
  const card = h('section', { class: 'paycard' },
    h('h2', { text: `Dankjewel, ${o.name}!` }),
    h('p', { text: `Scan de code met je bankapp, of schrijf het bedrag over met deze mededeling. Eén overschrijving voor: ${titles}.` }),
    statusNote(o),
    h('div', { class: 'slip' }, ok ? qrSvg(epcPayload(o.accountName, o.iban, o.totalCents, o.reference)) : null, fields),
    h('p', { class: 'muted small-text keep', text: 'Bewaar deze pagina of neem een screenshot.' }),
    h('div', { class: 'actions' }, later, paid));
  view.replaceChildren(card);
}

function renderMessage(view: HTMLElement, text: string): void {
  view.replaceChildren(add(h('section', { class: 'paycard' }),
    h('p', { text }),
    h('div', { class: 'actions' }, h('a', { class: 'btn', href: '#/' }, 'Naar de lijst'))));
}

export async function showPay(token: string): Promise<void> {
  const view = $('payView');
  view.replaceChildren(h('p', { class: 'empty', text: 'Je betaalgegevens worden geladen…' }));
  try {
    const order = await getOrder(token);
    if (order) renderOrder(view, order, token);
    else renderMessage(view, 'We vinden deze betaling niet terug. Kijk de link nog eens na.');
  } catch {
    renderMessage(view, 'Je betaalgegevens kunnen nu niet geladen worden. Probeer het straks opnieuw.');
  }
}
